import { z } from "zod";

import { targetAudienceSchema } from "./brand-profile.ts";

export const AUDIENCE_PROFILE_SOURCES = ["manual", "onboarding", "chat"] as const;
export type AudienceProfileSource = (typeof AUDIENCE_PROFILE_SOURCES)[number];
export const audienceProfileSourceSchema = z.enum(AUDIENCE_PROFILE_SOURCES);

export const ONBOARDING_AUDIENCE_PROFILE_DRAFTS_FIELD_PATH =
  "audienceProfiles.onboardingDrafts" as const;

export const audienceProfileTargetAudienceSchema = targetAudienceSchema.partial();
export type AudienceProfileTargetAudience = z.infer<typeof audienceProfileTargetAudienceSchema>;

const TARGET_AUDIENCE_FIELD_PREFIX = "targetAudience.";
const AUDIENCE_PROFILE_NAME_MAX_LENGTH = 160;
const AUDIENCE_PROFILE_DESCRIPTION_MAX_LENGTH = 2000;

const targetAudienceKeys = targetAudienceSchema.keyof().options;
const targetAudienceKeySet = new Set<string>(targetAudienceKeys);

export const AUDIENCE_PROFILE_TARGET_AUDIENCE_FIELD_PATHS = targetAudienceKeys.map(
  (key) => `${TARGET_AUDIENCE_FIELD_PREFIX}${key}`,
);

export type AudienceProfileV1 = {
  type: "audience_profile";
  version: 1;
  summary?: string;
  targetAudience: AudienceProfileTargetAudience;
};

export type AudienceProfileSnapshotV1 = {
  type: "audience_profile.snapshot";
  version: 1;
  id: string;
  brandId: string;
  name: string;
  description: string | null;
  source: AudienceProfileSource;
  profile: AudienceProfileV1;
  selectedAt: string;
};

const hasProfileContent = (value: {
  summary?: string;
  targetAudience?: AudienceProfileTargetAudience;
}): boolean =>
  Boolean(value.summary?.trim()) ||
  Object.values(value.targetAudience ?? {}).some((fieldValue) => {
    if (Array.isArray(fieldValue)) {
      return fieldValue.length > 0;
    }
    return fieldValue !== null && fieldValue !== undefined && fieldValue !== "";
  });

export const audienceProfileV1Schema: z.ZodType<AudienceProfileV1> = z
  .object({
    type: z.literal("audience_profile"),
    version: z.literal(1),
    summary: z.string().trim().min(1).max(4000).optional(),
    targetAudience: audienceProfileTargetAudienceSchema.default({}),
  })
  .superRefine((value, ctx) => {
    if (!hasProfileContent(value)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["targetAudience"],
        message: "Audience profile must include a summary or at least one audience attribute.",
      });
    }
  });

export const audienceProfileSnapshotV1Schema: z.ZodType<AudienceProfileSnapshotV1> = z.object({
  type: z.literal("audience_profile.snapshot"),
  version: z.literal(1),
  id: z.uuid(),
  brandId: z.uuid(),
  name: z.string().trim().min(1).max(160),
  description: z.string().trim().min(1).max(2000).nullable().default(null),
  source: audienceProfileSourceSchema,
  profile: audienceProfileV1Schema,
  selectedAt: z.iso.datetime({ offset: true }),
});

const omitLegacyAudienceContentAngles = (
  facts: Record<string, unknown> | null | undefined,
): Record<string, unknown> => {
  const { audienceContentAngles: _legacyAudienceContentAngles, ...currentFacts } = facts ?? {};
  return currentFacts;
};

export const audienceProfileSnapshotToPromptFacts = (
  snapshot: AudienceProfileSnapshotV1,
): Record<string, unknown> => {
  const targetAudience = omitLegacyAudienceContentAngles(
    snapshot.profile.targetAudience as Record<string, unknown>,
  );

  return {
    audienceProfileId: snapshot.id,
    audienceProfileName: snapshot.name,
    audienceProfileDescription: snapshot.description,
    audienceProfileSource: snapshot.source,
    summary: snapshot.profile.summary,
    ...targetAudience,
  };
};

export const audienceProfileSnapshotToAudienceFacts = (
  snapshot: AudienceProfileSnapshotV1,
): Record<string, unknown> => {
  const targetAudience = omitLegacyAudienceContentAngles(
    snapshot.profile.targetAudience as Record<string, unknown>,
  );

  return {
    summary: snapshot.profile.summary,
    ...targetAudience,
  };
};

export const buildAudiencePromptContext = (input: {
  selectedAudienceProfile?: AudienceProfileSnapshotV1 | null;
  generalAudienceFacts?: Record<string, unknown> | null;
}): {
  source: "selected_audience_profile" | "general_target_audience";
  selectedAudienceProfile: {
    id: string;
    name: string;
    description: string | null;
    source: AudienceProfileSource;
    selectedAt: string;
  } | null;
  facts: Record<string, unknown>;
} => {
  if (input.selectedAudienceProfile) {
    const snapshot = input.selectedAudienceProfile;
    return {
      source: "selected_audience_profile",
      selectedAudienceProfile: {
        id: snapshot.id,
        name: snapshot.name,
        description: snapshot.description,
        source: snapshot.source,
        selectedAt: snapshot.selectedAt,
      },
      facts: audienceProfileSnapshotToAudienceFacts(snapshot),
    };
  }

  return {
    source: "general_target_audience",
    selectedAudienceProfile: null,
    facts: omitLegacyAudienceContentAngles(input.generalAudienceFacts),
  };
};

export type AudienceProfileFactInput = {
  fieldPath: string;
  valueJson: unknown;
};

export type AudienceProfileDraftFromFacts = {
  name: string;
  description: string | null;
  profile: AudienceProfileV1;
};

const onboardingAudienceProfileStringSchema = z.string().trim().min(1);
const onboardingAudienceProfileStringListSchema = z
  .array(onboardingAudienceProfileStringSchema)
  .max(12);

export const onboardingAudienceProfileDraftV1Schema = z
  .object({
    type: z.literal("onboarding_audience_profile_draft"),
    version: z.literal(1),
    name: onboardingAudienceProfileStringSchema.max(AUDIENCE_PROFILE_NAME_MAX_LENGTH),
    summary: onboardingAudienceProfileStringSchema.max(AUDIENCE_PROFILE_DESCRIPTION_MAX_LENGTH),
    primaryAudienceSegments: onboardingAudienceProfileStringListSchema.min(1),
    secondaryAudienceSegments: onboardingAudienceProfileStringListSchema,
    painPoints: onboardingAudienceProfileStringListSchema,
    desiresAspirations: onboardingAudienceProfileStringListSchema,
    buyingTriggers: onboardingAudienceProfileStringListSchema,
    decisionMakingDrivers: onboardingAudienceProfileStringListSchema,
    demographics: onboardingAudienceProfileStringSchema.max(2000).nullable(),
    psychographics: onboardingAudienceProfileStringSchema.max(2000).nullable(),
    behavioralTraits: onboardingAudienceProfileStringSchema.max(2000).nullable(),
    awarenessStage: targetAudienceSchema.shape.awarenessStage.nullable(),
  })
  .strict();

export type OnboardingAudienceProfileDraftV1 = z.infer<
  typeof onboardingAudienceProfileDraftV1Schema
>;

export type ExistingAudienceProfileDraftAssignmentInput = {
  id: string;
  name: string;
};

export type AudienceProfileDraftAssignment = {
  draft: AudienceProfileDraftFromFacts;
  existing: ExistingAudienceProfileDraftAssignmentInput | null;
};

export const isAudienceProfileTargetAudienceFieldPath = (fieldPath: string): boolean => {
  if (!fieldPath.startsWith(TARGET_AUDIENCE_FIELD_PREFIX)) {
    return false;
  }

  return targetAudienceKeySet.has(fieldPath.slice(TARGET_AUDIENCE_FIELD_PREFIX.length));
};

const truncateText = (value: string, maxLength: number): string =>
  value.length > maxLength ? value.slice(0, maxLength - 1).trimEnd() : value;

const normalizeText = (value: unknown): string | null => {
  if (typeof value !== "string") {
    return null;
  }

  const normalized = value.replace(/\s+/g, " ").trim();
  return normalized || null;
};

const normalizeStringList = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.map((item) => normalizeText(item)).filter((item): item is string => Boolean(item))
    : [];

const normalizeComparableText = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .replace(/\s+/g, " ")
    .trim();

const areComparableAudienceLabelsOverlapping = (left: string, right: string): boolean => {
  const normalizedLeft = normalizeComparableText(left);
  const normalizedRight = normalizeComparableText(right);
  const leftTokens = new Set(normalizedLeft.split(" ").filter(Boolean));
  const rightTokens = new Set(normalizedRight.split(" ").filter(Boolean));
  const leftContainsRightTokens = [...rightTokens].every((token) => leftTokens.has(token));
  const rightContainsLeftTokens = [...leftTokens].every((token) => rightTokens.has(token));
  return (
    Boolean(normalizedLeft) &&
    Boolean(normalizedRight) &&
    (normalizedLeft === normalizedRight ||
      normalizedLeft.includes(normalizedRight) ||
      normalizedRight.includes(normalizedLeft) ||
      leftContainsRightTokens ||
      rightContainsLeftTokens)
  );
};

export const onboardingAudienceProfileDraftsV1Schema = z
  .array(onboardingAudienceProfileDraftV1Schema)
  .min(1)
  .max(4)
  .superRefine((drafts, ctx) => {
    for (const [index, draft] of drafts.entries()) {
      const priorDraft = drafts
        .slice(0, index)
        .find((candidate) => areComparableAudienceLabelsOverlapping(candidate.name, draft.name));
      if (priorDraft) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [index, "name"],
          message: `Audience profile names must be distinct; ${draft.name} overlaps ${priorDraft.name}.`,
        });
      }

      const segments = [...draft.primaryAudienceSegments, ...draft.secondaryAudienceSegments];
      for (const segment of segments) {
        const priorProfile = drafts
          .slice(0, index)
          .find((candidate) =>
            [...candidate.primaryAudienceSegments, ...candidate.secondaryAudienceSegments].some(
              (candidateSegment) =>
                areComparableAudienceLabelsOverlapping(candidateSegment, segment),
            ),
          );
        if (priorProfile) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [index, "primaryAudienceSegments"],
            message: `Audience profile segments must be distinct; ${segment} overlaps ${priorProfile.name}.`,
          });
        }
      }
    }
  });

const uniqueStringList = (items: readonly string[]): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const item of items) {
    const normalized = normalizeComparableText(item);
    if (!normalized || seen.has(normalized)) {
      continue;
    }

    seen.add(normalized);
    result.push(item);
  }

  return result;
};

const getAudienceProfileName = (targetAudience: AudienceProfileTargetAudience): string => {
  const segments = uniqueStringList([
    ...normalizeStringList(targetAudience.primaryAudienceSegments),
    ...normalizeStringList(targetAudience.secondaryAudienceSegments),
  ]);
  if (segments.length === 0) {
    return "Primary audience";
  }

  const visibleSegments = segments.slice(0, 3).join(", ");
  const suffix = segments.length > 3 ? ` +${segments.length - 3} more` : "";
  return truncateText(`${visibleSegments}${suffix}`, AUDIENCE_PROFILE_NAME_MAX_LENGTH);
};

const getAudienceProfileSummary = (
  targetAudience: AudienceProfileTargetAudience,
): string | undefined => {
  const explicitSummary = normalizeText(targetAudience.audienceProfileSummary);
  if (explicitSummary) {
    return explicitSummary;
  }

  const primarySegments = normalizeStringList(targetAudience.primaryAudienceSegments);
  if (primarySegments.length > 0) {
    return `Primary audience: ${primarySegments.join(", ")}.`;
  }

  return normalizeText(targetAudience.demographics) ?? undefined;
};

const isAudienceSegmentMatch = (segment: string, candidate: string): boolean => {
  return areComparableAudienceLabelsOverlapping(segment, candidate);
};

const buildProfileDraft = (
  targetAudience: AudienceProfileTargetAudience,
): AudienceProfileDraftFromFacts | null => {
  const summary = getAudienceProfileSummary(targetAudience);
  const parsedProfile = audienceProfileV1Schema.safeParse({
    type: "audience_profile",
    version: 1,
    ...(summary ? { summary } : {}),
    targetAudience,
  });
  if (!parsedProfile.success) {
    return null;
  }

  return {
    name: getAudienceProfileName(targetAudience),
    description: summary ? truncateText(summary, AUDIENCE_PROFILE_DESCRIPTION_MAX_LENGTH) : null,
    profile: parsedProfile.data,
  };
};

const buildRawTargetAudienceFromFacts = (
  facts: readonly AudienceProfileFactInput[],
): AudienceProfileTargetAudience | null => {
  const rawTargetAudience: Record<string, unknown> = {};

  for (const fact of facts) {
    if (!isAudienceProfileTargetAudienceFieldPath(fact.fieldPath)) {
      continue;
    }

    const key = fact.fieldPath.slice(TARGET_AUDIENCE_FIELD_PREFIX.length);
    if (rawTargetAudience[key] !== undefined) {
      continue;
    }

    const candidate = { ...rawTargetAudience, [key]: fact.valueJson };
    if (audienceProfileTargetAudienceSchema.safeParse(candidate).success) {
      rawTargetAudience[key] = fact.valueJson;
    }
  }

  const parsedTargetAudience = audienceProfileTargetAudienceSchema.safeParse(rawTargetAudience);
  if (!parsedTargetAudience.success) {
    return null;
  }

  return parsedTargetAudience.data;
};

const omitNullishAudienceFields = (value: Record<string, unknown>): AudienceProfileTargetAudience =>
  Object.fromEntries(
    Object.entries(value).filter(
      ([, fieldValue]) =>
        fieldValue !== null &&
        fieldValue !== undefined &&
        (!Array.isArray(fieldValue) || fieldValue.length > 0),
    ),
  ) as AudienceProfileTargetAudience;

const buildAudienceProfileDraftFromSynthesis = (
  draft: OnboardingAudienceProfileDraftV1,
  targetAudience: AudienceProfileTargetAudience,
): AudienceProfileDraftFromFacts | null => {
  const profileSegments = [...draft.primaryAudienceSegments, ...draft.secondaryAudienceSegments];
  const painPointMap = targetAudience.painPointMap?.filter((item) =>
    profileSegments.some((segment) => isAudienceSegmentMatch(segment, item.audienceSegment)),
  );
  const profileTargetAudience = omitNullishAudienceFields({
    primaryAudienceSegments: uniqueStringList(draft.primaryAudienceSegments),
    secondaryAudienceSegments: uniqueStringList(draft.secondaryAudienceSegments),
    painPoints: uniqueStringList(draft.painPoints),
    desiresAspirations: uniqueStringList(draft.desiresAspirations),
    buyingTriggers: uniqueStringList(draft.buyingTriggers),
    decisionMakingDrivers: uniqueStringList(draft.decisionMakingDrivers),
    demographics: draft.demographics,
    psychographics: draft.psychographics,
    behavioralTraits: draft.behavioralTraits,
    awarenessStage: draft.awarenessStage,
    geographicMarkets: targetAudience.geographicMarkets,
    audienceProfileSummary: draft.summary,
    painPointMap,
  });
  const parsedProfile = audienceProfileV1Schema.safeParse({
    type: "audience_profile",
    version: 1,
    summary: draft.summary,
    targetAudience: profileTargetAudience,
  });
  if (!parsedProfile.success) {
    return null;
  }

  return {
    name: truncateText(draft.name, AUDIENCE_PROFILE_NAME_MAX_LENGTH),
    description: truncateText(draft.summary, AUDIENCE_PROFILE_DESCRIPTION_MAX_LENGTH),
    profile: parsedProfile.data,
  };
};

export const buildAudienceProfileDraftsFromTargetAudienceFacts = (
  facts: readonly AudienceProfileFactInput[],
): AudienceProfileDraftFromFacts[] => {
  const targetAudience = buildRawTargetAudienceFromFacts(facts);
  if (!targetAudience) {
    return [];
  }

  const synthesisClaim = facts.find(
    (fact) => fact.fieldPath === ONBOARDING_AUDIENCE_PROFILE_DRAFTS_FIELD_PATH,
  );
  const synthesizedDrafts = onboardingAudienceProfileDraftsV1Schema.safeParse(
    synthesisClaim?.valueJson,
  );
  if (synthesizedDrafts.success) {
    const drafts = synthesizedDrafts.data.flatMap((draft) => {
      const profile = buildAudienceProfileDraftFromSynthesis(draft, targetAudience);
      return profile ? [profile] : [];
    });
    if (drafts.length === synthesizedDrafts.data.length) {
      return drafts;
    }
  }

  const fallbackDraft = buildProfileDraft(targetAudience);
  return fallbackDraft ? [fallbackDraft] : [];
};

export const buildAudienceProfileDraftFromTargetAudienceFacts = (
  facts: readonly AudienceProfileFactInput[],
): AudienceProfileDraftFromFacts | null => {
  return buildAudienceProfileDraftsFromTargetAudienceFacts(facts)[0] ?? null;
};

export const assignAudienceProfileDraftsToExistingProfiles = (
  drafts: readonly AudienceProfileDraftFromFacts[],
  existingProfiles: readonly ExistingAudienceProfileDraftAssignmentInput[],
): AudienceProfileDraftAssignment[] => {
  const usedExistingIds = new Set<string>();
  const existingByName = new Map<string, ExistingAudienceProfileDraftAssignmentInput>();
  for (const row of existingProfiles) {
    const key = normalizeComparableText(row.name);
    if (key && !existingByName.has(key)) {
      existingByName.set(key, row);
    }
  }

  return drafts.map((draft, index) => {
    const existingByExactName = existingByName.get(normalizeComparableText(draft.name));
    let existing =
      existingByExactName && !usedExistingIds.has(existingByExactName.id)
        ? existingByExactName
        : (existingProfiles[index] ?? null);
    if (existing && usedExistingIds.has(existing.id)) {
      existing = existingProfiles.find((row) => !usedExistingIds.has(row.id)) ?? null;
    }

    if (existing) {
      usedExistingIds.add(existing.id);
    }

    return {
      draft,
      existing: existing ?? null,
    };
  });
};
